import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { useAccount } from '../../../contexts/AccountContext';
import { PERMISSIONS } from '../../../shared/constants/permissions';
import type { Section } from '../../../types/tender';
import RichDiffViewer from '../../RichDiffViewer';
import RichTextEditor from '../../RichTextEditor';
import RichTextToolbar from '../../RichTextToolbar';
import SectionActionRow from './SectionItemComponents/SectionActionRow';
import SectionContentView from './SectionItemComponents/SectionContentView';
import SectionHeaderRow from './SectionItemComponents/SectionHeaderRow';

interface SectionItemProps {
  section: Section;
  tenderId: string;

  account: import('../../../types/auth').User | null;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, expanded: boolean) => void;
  isOwner?: boolean;
  canEditAllSections?: boolean;
  // State & Handlers
  editingSectionId: string | null;
  editContent: string;
  setEditContent: (content: string) => void;
  activeEditor: Editor | null;

  setActiveEditor: (editor: Editor | null) => void;
  showDiffs: Record<string, boolean>;
  setShowDiffs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  inlineResolved: Record<string, boolean>;
  chatSectionId: string | null;
  setChatSectionId: (id: string | null) => void;
  downloadingSectionId: string | null;
  isHistoryComparison?: boolean;

  handlers: {
    handleSave: (section: Section) => Promise<boolean>;
    handleCancelEdit: (section: Section) => Promise<void>;
    handleStatusChange: (section: Section, status: string) => void;
    handleDeleteClick: (section: Section) => void;
    handleOpenAssignDialog: (section: Section) => void;
    assign: (
      section: Section,
      user: Record<string, unknown> | null,
      permissions?: string[]
    ) => Promise<void>;
    handleRejectSuggestion: (section: Section) => void;
    handleAcceptSuggestion: (section: Section) => void;
    handleDiffApply: (sectionId: string, oldVal: string, newVal: string) => Promise<void>;
    handleUndo: (section: Section) => Promise<void>;
    handleRedo: (section: Section) => Promise<void>;
    handleOpenManualEdit: (section: Section) => void;
    handleOpenAIEdit: (section: Section) => Promise<void>;
    handleDownloadSectionDocx: (section: Section) => Promise<void>;
    handleApplyAIEdit: (content: string) => Promise<void>;
    handleReceiveSuggestion: (suggestion: string) => Promise<void>;
    handleUnlock: (section: Section) => Promise<void>;
    handleOpenHistory: (section: Section) => Promise<void>;
    handleRestoreRevision?: (section: Section) => Promise<void>;
    handleCancelHistoryComparison?: (section: Section) => Promise<void>;
  };
}

const SectionItem: React.FC<SectionItemProps> = ({
  section,
  tenderId: _tenderId,
  account,
  expanded,
  onChange,
  isOwner,
  canEditAllSections,
  editingSectionId,
  editContent,
  setEditContent,
  activeEditor,
  setActiveEditor,
  showDiffs,
  setShowDiffs,
  inlineResolved,
  chatSectionId,
  setChatSectionId,
  downloadingSectionId,
  handlers,
  isHistoryComparison,
}) => {
  const theme = useTheme();
  const { hasPermission } = useAccount();

  const shouldShowDiff = (id: string) => showDiffs[id] ?? true;

  const [statusAnchorEl, setStatusAnchorEl] = React.useState<null | HTMLElement>(null);

  const openStatusMenu = Boolean(statusAnchorEl);

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusSelect = (status: string) => {
    // biome-ignore lint/suspicious/noExplicitAny: complex type
    handlers.handleStatusChange(section, status as any);

    handleStatusClose();
  };

  const canBreakLock =
    section.lockedBy &&
    section.lockedBy.uid !== account?.uid &&
    hasPermission(PERMISSIONS.TENDER_MANAGE_TEAM);

  const canManageAssignments = isOwner || hasPermission(PERMISSIONS.TENDER_MANAGE_TEAM);
  const canEditSection = canEditAllSections || section.assignedTo?.uid === account?.uid;

  return (
    <Accordion
      expanded={expanded}
      TransitionProps={{
        unmountOnExit: true,

        onEntered: (node: HTMLElement | null) => {
          if (node) {
            node.style.overflow = 'visible';
          }
        },

        onExit: (node: HTMLElement | null) => {
          if (node) {
            node.style.overflow = 'hidden';
          }
        },
      }}
      onChange={onChange}
      sx={{
        mb: 0.5,

        '&:hover': {
          mb: 1,
        },

        bgcolor: 'background.paper',

        backgroundImage: 'none',

        ml: expanded ? 0 : section.level && section.level > 1 ? `${(section.level - 1) * 3}rem` : 0,

        width: expanded
          ? '100%'
          : section.level && section.level > 1
            ? `calc(100% - ${(section.level - 1) * 3}rem)`
            : '100%',

        border: 2,

        borderColor:
          section.lockedBy && section.lockedBy.uid !== account?.uid
            ? theme.palette.error.main
            : chatSectionId === section.id || editingSectionId === section.id
              ? theme.palette.bidlogiq.orange
              : section.suggested_content
                ? theme.palette.bidlogiq.orange
                : theme.palette.greyContrast || 'divider',

        borderRadius: 2,

        overflow: 'hidden',

        transition: 'all 0.2s ease',

        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        sx={{
          position: 'sticky',

          top: 0,

          zIndex: 30,

          bgcolor: 'background.paper',

          borderBottom: 1,

          borderColor: 'divider',

          boxShadow: 1,

          '& .MuiAccordionSummary-content': {
            flexWrap: 'wrap',

            flexDirection: 'column',

            alignItems: 'flex-start',

            width: '100%',

            // Ensure handle is visible

            '& .drag-handle': {
              visibility: 'visible',

              opacity: 0.5,

              '&:hover': { opacity: 1 },
            },
          },
        }}
      >
        <SectionHeaderRow
          section={section}
          account={account}
          isOwner={!!isOwner}
          canManageAssignments={canManageAssignments}
          canBreakLock={!!canBreakLock}
          isHistoryComparison={!!isHistoryComparison}
          statusAnchorEl={statusAnchorEl}
          openStatusMenu={openStatusMenu}
          handleStatusClick={handleStatusClick}
          handleStatusClose={handleStatusClose}
          handleStatusSelect={handleStatusSelect}
          handlers={handlers}
        />
        {/* Row 2: Actions & Revisions */}
        {expanded && (
          <SectionActionRow
            section={section}
            account={account}
            canEditSection={!!canEditSection}
            isHistoryComparison={!!isHistoryComparison}
            shouldShowDiff={shouldShowDiff}
            setShowDiffs={setShowDiffs}
            inlineResolved={inlineResolved}
            chatSectionId={chatSectionId}
            setChatSectionId={setChatSectionId}
            editingSectionId={editingSectionId}
            downloadingSectionId={downloadingSectionId}
            handlers={handlers}
          />
        )}

        {/* Editor Toolbar - Visible when editing */}
        {editingSectionId === section.id && activeEditor && (
          <Box
            sx={{ width: '100%', mt: 1, borderTop: 1, borderColor: 'divider' }}
            onClick={(e) => e.stopPropagation()}
          >
            <RichTextToolbar editor={activeEditor} />
          </Box>
        )}
      </AccordionSummary>

      {/* Accordion Content */}
      <AccordionDetails sx={{ bgcolor: 'background.paper', p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Main Content Area */}
          <Box sx={{ p: 2 }}>
            {editingSectionId === section.id ? (
              <RichTextEditor
                key={section.id}
                value={editContent}
                onChange={setEditContent}
                onCreate={(editor) => setActiveEditor(editor)}
                maxLength={5000}
                stickyOffset={70}
                hideToolbar={true}
              />
            ) : section.suggested_content ? (
              shouldShowDiff(section.id) ? (
                <RichDiffViewer
                  oldText={section.content}
                  newText={section.suggested_content}
                  onApply={(newOld, newNew) => handlers.handleDiffApply(section.id, newOld, newNew)}
                />
              ) : (
                <SectionContentView content={section.suggested_content} />
              )
            ) : (
              <SectionContentView content={section.content} lastEditedBy={section.lastEditedBy} />
            )}
            {/* Inline Source Chips */}
            {section.sources && section.sources.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  borderTop: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <Tooltip title="These sources were used to generate this section and all its nested sub-sections.">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mr: 1,
                      fontSize: '0.75rem',
                      cursor: 'help',
                    }}
                  >
                    Sources used <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5 }} />:
                  </Typography>
                </Tooltip>
                {section.sources.map((source) => (
                  <Chip
                    key={source}
                    label={source}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SectionItem;
